import React, { useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { ExtraPaymentLineData, OrderData, OrderService } from '../api';
import { ColorButton } from './ui/ConfimationDialog';
import { useSafeExecute } from './apiNotifications/safeExecute';
import { useGetAccountAuthenticated } from '../session/swr/useGetAccountAuthenticated';

const T_PREFIX = 'calendar.edit_order.order_details.extraPayment';

type EditExtraPaymentLineDialogProps = {
  orderId: string;
  line: ExtraPaymentLineData;
  onClose: () => void;
  onCommitted: (freshOrder: OrderData) => void;
};

export const EditExtraPaymentLineDialog = ({
  orderId,
  line,
  onClose,
  onCommitted,
}: EditExtraPaymentLineDialogProps) => {
  const { t } = useTranslation();
  const safeExecute = useSafeExecute();
  const [title, setTitle] = useState(line.title);
  const [price, setPrice] = useState<number | ''>(line.price);
  const { account } = useGetAccountAuthenticated({});
  const paymentMethods = account?.other_payment_methods ?? [];
  const initialPaymentMethodId = line.payment_method_id ?? '';
  const [paymentMethodId, setPaymentMethodId] = useState(initialPaymentMethodId);
  const [isSaving, setIsSaving] = useState(false);

  const isValid = title.trim().length > 0 && price !== '' && price >= 0;

  const save = async () => {
    setIsSaving(true);
    try {
      const freshOrder = await safeExecute({
        apiCall: () =>
          OrderService.updateOrderExtraPayment({
            order_id: orderId,
            line_id: line.id!,
            amount: Number(price),
            label: title.trim(),
            payment_method_id: paymentMethodId !== initialPaymentMethodId ? paymentMethodId : undefined,
          }),
        successMessage: t(`${T_PREFIX}.lineUpdated`),
      });
      onCommitted(freshOrder);
      onClose();
    } catch {
      // safeExecute already surfaced the failure; keep the dialog open so the admin can retry.
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>{t(`${T_PREFIX}.editLine`)}</DialogTitle>
      <DialogContent>
        <Grid container flexDirection="column" flexWrap="nowrap" gap={2}>
          <TextField
            size="small"
            label={t('calendar.edit_order.description')}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
          <TextField
            size="small"
            type="number"
            label={t('calendar.edit_order.priceWord')}
            value={price}
            inputProps={{ min: 0 }}
            onChange={(event) => setPrice(event.target.value === '' ? '' : Number(event.target.value))}
          />
          {(paymentMethods.length > 0 || !!line.payment_method_id) && (
            <TextField
              select
              size="small"
              label={t(`${T_PREFIX}.paymentMethod`)}
              value={paymentMethodId}
              onChange={(event) => setPaymentMethodId(event.target.value)}>
              <MenuItem value="">{t(`${T_PREFIX}.noPaymentMethod`)}</MenuItem>
              {line.payment_method_id && !paymentMethods.some((method) => method.id === line.payment_method_id) && (
                <MenuItem value={line.payment_method_id} disabled>
                  {line.payment_method_label}
                </MenuItem>
              )}
              {paymentMethods.map((method) => (
                <MenuItem key={method.id} value={method.id}>
                  {method.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <ColorButton onClick={onClose}>{t('cancel')}</ColorButton>
        <LoadingButton
          loading={isSaving}
          disabled={!isValid}
          variant="contained"
          onClick={save}
          sx={{ boxShadow: 'none' }}>
          {t('save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
